import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button, Card, Form, ListGroup } from 'react-bootstrap';

const DualListBox = ({
  availableItems,
  selectedItems,
  onChange,
  availableTitle = 'Available Items',
  selectedTitle = 'Selected Items',
  searchable = true,
  sortable = true,
  className = ''
}) => {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedSelected, setSelectedSelected] = useState([]);

  useEffect(() => {
    // Initialize available items (exclude already selected ones)
    const selectedIds = selectedItems.map(item => item.fieldId);
    const filteredAvailable = availableItems.filter(
      item => !selectedIds.includes(item.fieldId)
    );
    setAvailable(filteredAvailable);
    setSelected(selectedItems);
  }, [availableItems, selectedItems]);

  const handleSelectAvailable = item => {
    const isSelected = selectedAvailable.some(i => i.fieldId === item.fieldId);
    if (isSelected) {
      setSelectedAvailable(
        selectedAvailable.filter(i => i.fieldId !== item.fieldId)
      );
    } else {
      setSelectedAvailable([...selectedAvailable, item]);
    }
  };

  const handleSelectSelected = item => {
    const isSelected = selectedSelected.some(i => i.fieldId === item.fieldId);
    if (isSelected) {
      setSelectedSelected(
        selectedSelected.filter(i => i.fieldId !== item.fieldId)
      );
    } else {
      setSelectedSelected([...selectedSelected, item]);
    }
  };

  const handleDoubleClick = (item, fromAvailable) => {
    if (fromAvailable) {
      moveToSelected([item]);
    } else {
      moveToAvailable([item]);
    }
  };

  const moveToSelected = items => {
    const newSelected = [...selected, ...items];
    const newAvailable = available.filter(
      item => !items.some(i => i.fieldId === item.fieldId)
    );

    setSelected(newSelected);
    setAvailable(newAvailable);
    setSelectedAvailable([]);
    onChange(newSelected);
  };

  const moveToAvailable = items => {
    const newAvailable = [...available, ...items];
    const newSelected = selected.filter(
      item => !items.some(i => i.fieldId === item.fieldId)
    );

    setAvailable(newAvailable);
    setSelected(newSelected);
    setSelectedSelected([]);
    onChange(newSelected);
  };

  const moveAllToSelected = () => {
    const filtered = getFilteredAvailable();
    moveToSelected(filtered);
  };

  const moveAllToAvailable = () => {
    const filtered = getFilteredSelected();
    moveToAvailable(filtered);
  };

  const moveUp = () => {
    if (selectedSelected.length !== 1) return;

    const item = selectedSelected[0];
    const index = selected.findIndex(i => i.fieldId === item.fieldId);
    if (index > 0) {
      const newSelected = [...selected];
      [newSelected[index - 1], newSelected[index]] = [
        newSelected[index],
        newSelected[index - 1]
      ];
      setSelected(newSelected);
      onChange(newSelected);
    }
  };

  const moveDown = () => {
    if (selectedSelected.length !== 1) return;

    const item = selectedSelected[0];
    const index = selected.findIndex(i => i.fieldId === item.fieldId);
    if (index < selected.length - 1) {
      const newSelected = [...selected];
      [newSelected[index], newSelected[index + 1]] = [
        newSelected[index + 1],
        newSelected[index]
      ];
      setSelected(newSelected);
      onChange(newSelected);
    }
  };

  const getFilteredAvailable = () => {
    return available.filter(
      item =>
        item.displayName &&
        item.displayName.toLowerCase().includes(availableSearch.toLowerCase())
    );
  };

  const getFilteredSelected = () => {
    return selected.filter(
      item =>
        item.displayName &&
        item.displayName.toLowerCase().includes(selectedSearch.toLowerCase())
    );
  };

  return (
    <div className={`dual-list-box ${className}`}>
      <Flex gap={4}>
        {/* Available Items */}
        <div className="flex-1">
          <Card className="border-0 shadow-sm">
            <Card.Header className="text-white border-0">
              <h6 className="mb-0 d-flex align-items-center">
                <FontAwesomeIcon icon="list" className="me-2" />
                {availableTitle}
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              {searchable && (
                <div className="p-3 border-bottom">
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar colunas..."
                    size="sm"
                    value={availableSearch}
                    onChange={e => setAvailableSearch(e.target.value)}
                    className="border-2"
                  />
                </div>
              )}
              <ListGroup
                variant="flush"
                style={{ height: '300px', overflowY: 'auto' }}
              >
                {getFilteredAvailable().map(item => (
                  <ListGroup.Item
                    key={item.fieldId}
                    action
                    active={selectedAvailable.some(
                      i => i.fieldId === item.fieldId
                    )}
                    onClick={() => handleSelectAvailable(item)}
                    onDoubleClick={() => handleDoubleClick(item, true)}
                    className="cursor-pointer border-0 hover-bg-light"
                    style={{ transition: 'all 0.2s ease' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-medium">{item.displayName}</span>
                      <small
                        className={`badge ${
                          selectedAvailable.some(
                            i => i.fieldId === item.fieldId
                          )
                            ? 'bg-light text-primary'
                            : 'bg-secondary'
                        }`}
                      >
                        {item.dataType}
                      </small>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </div>

        {/* Control Buttons */}
        <div className="mx-3 d-flex flex-column justify-content-center gap-3">
          <Button
            size="sm"
            variant="falcon-default"
            className="rounded-1"
            onClick={() => moveToSelected(selectedAvailable)}
            disabled={selectedAvailable.length === 0}
            title="Mover selecionados"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
          <Button
            size="sm"
            variant="falcon-default"
            className="rounded-1"
            onClick={moveAllToSelected}
            disabled={getFilteredAvailable().length === 0}
            title="Mover todos"
          >
            <FontAwesomeIcon icon={faChevronRight} />
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
          <Button
            size="sm"
            variant="falcon-default"
            className="rounded-1"
            onClick={() => moveToAvailable(selectedSelected)}
            disabled={selectedSelected.length === 0}
            title="Mover de volta"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          <Button
            size="sm"
            variant="falcon-default"
            className="rounded-1"
            onClick={moveAllToAvailable}
            disabled={getFilteredSelected().length === 0}
            title="Mover todos de volta"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
        </div>

        {/* Selected Items */}
        <div className="flex-1">
          <Card className="border-0 shadow-sm">
            <Card.Header className="text-white border-0">
              <Flex justifyContent="between" alignItems="center">
                <h6 className="mb-0 me-2 d-flex align-items-center">
                  <FontAwesomeIcon icon="check-circle" className="me-2" />
                  {selectedTitle}
                  {selected.length === 0 && (
                    <small className="ms-2 text-danger">
                      <FontAwesomeIcon
                        icon="exclamation-triangle"
                        className="me-1"
                      />
                      Obrigatório
                    </small>
                  )}
                </h6>
                {sortable && (
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="light"
                      onClick={moveUp}
                      disabled={selectedSelected.length !== 1}
                      className="p-1"
                      title="Mover para cima"
                      style={{ width: '30px', height: '30px' }}
                    >
                      <FontAwesomeIcon icon="arrow-up" size="xs" />
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      onClick={moveDown}
                      disabled={selectedSelected.length !== 1}
                      className="p-1"
                      title="Mover para baixo"
                      style={{ width: '30px', height: '30px' }}
                    >
                      <FontAwesomeIcon icon="arrow-down" size="xs" />
                    </Button>
                  </div>
                )}
              </Flex>
            </Card.Header>
            <Card.Body className="p-0">
              {searchable && (
                <div className="p-3 border-bottom">
                  <Form.Control
                    type="text"
                    placeholder="Pesquisar colunas..."
                    size="sm"
                    value={selectedSearch}
                    onChange={e => setSelectedSearch(e.target.value)}
                    className="border-2"
                  />
                </div>
              )}
              <ListGroup
                variant="flush"
                style={{ height: '300px', overflowY: 'auto' }}
              >
                {getFilteredSelected().length === 0 ? (
                  <ListGroup.Item className="text-center text-muted py-5 border-0">
                    <FontAwesomeIcon
                      icon="exclamation-triangle"
                      size="2x"
                      className="mb-2 text-danger"
                    />
                    <p className="mb-1 fw-semibold">
                      Nenhuma coluna selecionada
                    </p>
                    <small className="text-danger">
                      <FontAwesomeIcon
                        icon="exclamation-triangle"
                        className="me-1"
                      />
                      Pelo menos uma coluna é obrigatória
                    </small>
                  </ListGroup.Item>
                ) : (
                  getFilteredSelected().map((item, index) => (
                    <ListGroup.Item
                      key={item.fieldId}
                      action
                      active={selectedSelected.some(
                        i => i.fieldId === item.fieldId
                      )}
                      onClick={() => handleSelectSelected(item)}
                      onDoubleClick={() => handleDoubleClick(item, false)}
                      className="cursor-pointer border-0 hover-bg-light"
                      style={{ transition: 'all 0.2s ease' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-medium">
                          <span className="text-muted me-2">{index + 1}.</span>
                          {item.displayName}
                        </span>
                        <small
                          className={`badge ${
                            selectedSelected.some(
                              i => i.fieldId === item.fieldId
                            )
                              ? 'bg-light text-success'
                              : 'bg-secondary'
                          }`}
                        >
                          {item.dataType}
                        </small>
                      </div>
                    </ListGroup.Item>
                  ))
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </div>
      </Flex>
    </div>
  );
};

DualListBox.propTypes = {
  availableItems: PropTypes.arrayOf(
    PropTypes.shape({
      fieldId: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      fieldId: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  availableTitle: PropTypes.string,
  selectedTitle: PropTypes.string,
  searchable: PropTypes.bool,
  sortable: PropTypes.bool,
  className: PropTypes.string
};

export default DualListBox;
